import { CircleX, FileLock2 } from 'lucide-react';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useParams } from 'react-router';
import { Logo } from '@/components/brand/Logo';
import { Badge, Button, Field, Input, LoadingState } from '@/components/ui';
import {
  useOpenShareCode,
  useSharedLink,
  type SharedRecord,
} from '@/features/record-share/api';
import { isApiError } from '@/lib/api';
import { dayLabel, whenLabel } from '@/pages/ops/register-format';
import verify from '@/pages/ops/mc.module.css';
import styles from './share.module.css';

/** What the outside doctor reads when a share cannot be opened. */
function problemText(err: unknown): { title: string; body: string } {
  if (!isApiError(err))
    return {
      title: 'The record could not be opened',
      body: 'Check your connection and try again.',
    };
  // Wrong tries from this network lock it for 15 minutes; the plain rate
  // limit clears within a minute.
  if (err.code === 'SHARE_TOO_MANY_ATTEMPTS')
    return {
      title: 'Too many wrong tries',
      body: 'Wait 15 minutes, then try again.',
    };
  if (err.status === 429)
    return {
      title: 'Too many requests',
      body: 'Wait a minute, then try again.',
    };
  if (err.code === 'SHARE_CODE_INVALID')
    return {
      title: 'The code or date of birth is not right',
      body: 'Check both with the patient. After 5 wrong dates of birth the code stops working.',
    };
  if (err.status === 400)
    return {
      title: 'The code or date of birth is not right',
      body: 'Check both with the patient.',
    };
  if (err.status === 404 || err.status === 410)
    return {
      title: err.problem?.title ?? 'This share does not work',
      body: err.problem?.detail ?? 'Ask the patient for a new one.',
    };
  return {
    title: 'The record could not be opened',
    body: 'Try again in a moment.',
  };
}

/** The share page is private health data: no search indexing, no Referer. */
function usePrivatePage() {
  useEffect(() => {
    const title = document.title;
    document.title = 'Shared health summary · Ommri Care';
    const metas = [
      ['robots', 'noindex, nofollow'],
      ['referrer', 'no-referrer'],
    ]
      .filter(([name]) => !document.head.querySelector(`meta[name="${name}"]`))
      .map(([name, content]) => {
        const m = document.createElement('meta');
        m.name = name;
        m.content = content;
        document.head.appendChild(m);
        return m;
      });
    return () => {
      document.title = title;
      metas.forEach((m) => m.remove());
    };
  }, []);
}

/**
 * RL-1911 (gap #57): the page an outside doctor opens. `/share/:token` opens
 * a 72-hour link; `/share` takes a one-time code with the patient's date of
 * birth. No sign-in; each opening is logged in the patient's app.
 */
export function SharePage() {
  usePrivatePage();
  const { token = '' } = useParams();
  return (
    <main className={verify.vpage}>
      <div className={`${verify.vwrap} ${styles.wrap}`}>
        <div className={verify.vtop}>
          <Logo />
          <span>Shared health summary</span>
        </div>
        {token ? <LinkShare token={token} /> : <CodeShare />}
        <p className={verify.vfoot}>
          Shared by the patient through the Ommri Care app. Do not forward this
          page; ask the patient to share again if someone else needs it.
        </p>
      </div>
    </main>
  );
}

function Problem({ err }: { err: unknown }) {
  const p = problemText(err);
  return (
    <div className={`${verify.vban} ${verify.bad}`} role="alert">
      <CircleX aria-hidden="true" />
      <div>
        <b>{p.title}</b>
        <span>{p.body}</span>
      </div>
    </div>
  );
}

function LinkShare({ token }: { token: string }) {
  const q = useSharedLink(token);
  if (q.isLoading) return <LoadingState />;
  if (q.error || !q.data) return <Problem err={q.error} />;
  return <SharedRecordView record={q.data} />;
}

function CodeShare() {
  const open = useOpenShareCode();
  const [code, setCode] = useState('');
  const [dateOfBirth, setDob] = useState('');
  const [missing, setMissing] = useState(false);
  if (open.data) return <SharedRecordView record={open.data} />;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const c = code.replace(/[\s-]/g, '').toUpperCase();
    if (!c || !/^\d{4}-\d{2}-\d{2}$/.test(dateOfBirth)) {
      setMissing(true);
      return;
    }
    setMissing(false);
    open.mutate({ code: c, dateOfBirth });
  };
  return (
    <form className={styles.card} onSubmit={submit} noValidate>
      <h1 className={styles.h1}>Open a shared record</h1>
      <p className={styles.muted}>
        Type the code the patient gave you and their date of birth. A code opens
        the record once.
      </p>
      <Field
        label="Share code"
        error={missing && !code.trim() ? 'Enter the code.' : undefined}
      >
        <Input
          value={code}
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          maxLength={12}
          onChange={(e) => setCode(e.target.value)}
        />
      </Field>
      <Field
        label="Patient's date of birth"
        error={
          missing && !/^\d{4}-\d{2}-\d{2}$/.test(dateOfBirth)
            ? 'Enter the date of birth.'
            : undefined
        }
      >
        <Input
          type="date"
          value={dateOfBirth}
          onChange={(e) => setDob(e.target.value)}
        />
      </Field>
      {open.error && <Problem err={open.error} />}
      <Button type="submit" variant="primary" loading={open.isPending}>
        Open record
      </Button>
    </form>
  );
}

const sexLabel = { male: 'Male', female: 'Female' } as const;

/** RL-1912: the shared summary, read only. */
export function SharedRecordView({ record }: { record: SharedRecord }) {
  const p = record.patient;
  // The form that opened it is gone: move focus to the record (code flow).
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    if (record.sharedVia === 'code') heading.current?.focus();
  }, [record.sharedVia]);
  return (
    <article className={styles.card} aria-label="Shared health summary">
      <header className={styles.head}>
        <FileLock2 aria-hidden="true" />
        <div>
          <h1 className={styles.h1} ref={heading} tabIndex={-1}>
            {p.name}
          </h1>
          <p className={styles.muted}>
            {[
              p.dateOfBirth && `Born ${dayLabel(p.dateOfBirth)}`,
              p.sex && sexLabel[p.sex],
              p.identityMasked &&
                `${p.identityType === 'passport' ? 'Passport' : 'MyKad'} ${p.identityMasked}`,
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
        </div>
      </header>
      <p className={styles.meta}>
        {record.sharedVia === 'code'
          ? 'Opened with a one-time code'
          : `Link works until ${whenLabel(record.expiresAt)}`}
        {` · as of ${whenLabel(record.generatedAt)}`}
      </p>

      <section aria-labelledby="sh-allergies">
        <h2 id="sh-allergies" className={styles.h2}>
          Allergies
        </h2>
        {record.allergies.length ? (
          <ul className={styles.list}>
            {record.allergies.map((a, i) => (
              <li key={i}>
                <strong>{a.substance}</strong>
                {a.reaction && ` · ${a.reaction}`}
                {a.severity && ` · ${a.severity}`}{' '}
                <Badge tone={a.status === 'confirmed' ? 'danger' : 'warning'}>
                  {a.status === 'confirmed'
                    ? 'Confirmed'
                    : 'Reported by patient'}
                </Badge>
              </li>
            ))}
          </ul>
        ) : (
          <p className={styles.muted}>No allergies recorded.</p>
        )}
      </section>

      <section aria-labelledby="sh-medicines">
        <h2 id="sh-medicines" className={styles.h2}>
          Medicines (last {record.periodMonths} months)
        </h2>
        {record.medicines.length ? (
          <ul className={styles.list}>
            {record.medicines.map((m, i) => (
              <li key={i}>
                <strong>{m.medicine}</strong>
                {[
                  m.dose,
                  m.frequency,
                  m.durationDays != null && `${m.durationDays} days`,
                ]
                  .filter(Boolean)
                  .map((x) => ` · ${x}`)
                  .join('')}
                {m.prescribedOn && (
                  <span className={styles.muted}>
                    {` · prescribed ${dayLabel(m.prescribedOn)}`}
                  </span>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className={styles.muted}>No medicines prescribed.</p>
        )}
      </section>

      <section aria-labelledby="sh-results">
        <h2 id="sh-results" className={styles.h2}>
          Results (last {record.periodMonths} months)
        </h2>
        {record.results.length ? (
          <table className={styles.table}>
            <thead>
              <tr>
                <th scope="col">Test</th>
                <th scope="col">Result</th>
                <th scope="col">Range</th>
                <th scope="col">Date</th>
              </tr>
            </thead>
            <tbody>
              {record.results.map((r, i) => (
                <tr key={i}>
                  <td data-label="Test">{r.testName}</td>
                  <td data-label="Result">
                    {[r.value, r.unit].filter(Boolean).join(' ') || '-'}
                    {r.flag && r.flag !== 'normal' && (
                      <>
                        {' '}
                        <Badge tone="warning">{r.flag}</Badge>
                      </>
                    )}
                  </td>
                  <td data-label="Range">{r.referenceRange ?? '-'}</td>
                  <td data-label="Date">
                    {r.resultDate ? dayLabel(r.resultDate) : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className={styles.muted}>No results released.</p>
        )}
      </section>

      {record.unavailable.length > 0 && (
        <p className={styles.muted}>
          Not available right now: {record.unavailable.join(', ')}.
        </p>
      )}
      <p className={styles.notice}>{record.notice}</p>
    </article>
  );
}
